import { queryClient } from '@/http/query-client';
import { http } from '@/http/http';
import { Endpoints } from '@/http/endpoints';
import { clearRumTokenCache } from '@/auth/rum-token';
import { clearRoleScreens, ensureRoleScreens, isScreenAllowed } from '@/auth/role-screen-access';
import {
  ensureAppRegistry,
  ensureAdminAppRegistry,
  isTileProvisioned,
  RAW_TILES_QUERY_KEY,
} from '@/apps/use-app-tiles';
import { useClientStore, type ClientInfo } from '@/client/client-store';
import { CLIENTS_QUERY_KEY, fetchUserClients } from '@/client/use-clients';
import { navigateToClientScope, parseClientScopedApp } from '@/router/client-navigation';
import { closePopupMfe } from '@/mfe/popup-store';
import { postClientRefresh } from './broadcast';
import { displayLoader, hideLoader } from '@/ui/loader-store';
import { pushToast } from '@/ui/toast-store';
import { eventsModule } from '@/events/events-module';
import { getRouterInstance } from '@/router/router-instance';
import { markInteractionRequired } from '@/auth/interaction-required';

/** Lightweight cache invalidation for soft MFE remount (no role re-resolution). */
export function refreshActiveClientContext(): void {
  const clientId = useClientStore.getState().current?.id;
  // Scope invalidation to the current client so stale caches for previously
  // visited clients are not eagerly refetched — they refresh on demand when
  // the user switches back.
  void queryClient.invalidateQueries({ queryKey: ['rumRolesForProfile', clientId] });
  void queryClient.invalidateQueries({ queryKey: [RAW_TILES_QUERY_KEY, clientId] });
  void queryClient.invalidateQueries({ queryKey: ['app-tiles', clientId] });
  void queryClient.invalidateQueries({ queryKey: ['header-icon-tiles', clientId] });
}

/** Full portal refresh mirroring the Angular `refreshClientList()` + CLIENT.REFRESH chain. */
export async function fullPortalRefresh(): Promise<void> {
  // localStorage is the source of truth: an MFE/orchestrator may write the
  // active client directly, ahead of the Zustand store.
  let clientId = localStorage.getItem('currentlySelectedClientId') || '';
  const prevClientId = clientId; // captured before any default resolution

  // The store's pre-refresh client is the true "before" value for cross-tab
  // change detection: an orchestrator task-start writes the new client to
  // localStorage BEFORE calling EY_RAPToR_PortalRefresh, so by the time we run
  // localStorage already holds the NEW client and can't reveal the change alone.
  const storePrevClientId = useClientStore.getState().current?.id ?? null;
  // True when we land on clients[0] (or the no-storage default) instead of the
  // requested client — an access fallback, not an intended switch. Used to
  // suppress the cross-tab broadcast so a failed/inaccessible refresh never
  // force-moves peer tabs (Angular never propagates the fallback either).
  let resolvedViaFallback = false;

  void queryClient.invalidateQueries();
  displayLoader('Refreshing Clients..');

  // No active client in storage (e.g. it was cleared): resolve a default and
  // write it back, mirroring Angular's getClientsFromRum(refreshsession) which
  // sets currentlySelectedClientId to clients[0] when none is stored.
  if (!clientId) {
    const store = useClientStore.getState();
    let clients = store.available;
    if (clients.length === 0) {
      try {
        clients = await fetchUserClients();
      } catch {
        clients = [];
      }
    }
    // Angular restores clients[0] (first RUM client), not the in-memory
    // current, so match that exactly.
    const fallback = clients[0];
    if (!fallback) {
      hideLoader();
      eventsModule.emit('shell:portal-refresh');
      return;
    }
    // setCurrent early-returns without persisting when the id is unchanged, so
    // write storage explicitly to guarantee the restore. Commit silently — this
    // is a recovery default, not an intended switch, so it must not broadcast.
    localStorage.setItem('currentlySelectedClientId', fallback.id);
    if (store.current?.id !== fallback.id) {
      store.setCurrent(fallback, { source: 'reset', silent: true });
    }
    clientId = fallback.id;
    resolvedViaFallback = true;
  }

  window.dispatchEvent(new CustomEvent('EY.RAPToR.Client.Refresh', { detail: clientId }));

  // Current app + legacy screen path from `/{clientId}/app-container/...`,
  // captured before we reconcile/navigate. Parsing must handle the
  // `workspace/apps/{appId}` and `admin/apps/{appId}` mount forms (Angular
  // `getCurrentURLSegement` parity): a naive `app-container/([^/]+)` capture
  // returns the SCOPE segment (`workspace`/`admin`), which made the tile gate
  // below check `workspace/apps/workspace` (never provisioned) and bounce every
  // workspace/admin app to home on refresh.
  const { appId: currentAppId, screenPath: currentScreenPath } = parseClientScopedApp(
    window.location.pathname,
  );

  clearRumTokenCache();
  clearRoleScreens();

  try {
    // Refetch the RUM client list first so we can validate the stored client
    // against it before resolving roles (Angular's getClientsFromRum ordering).
    // Scope all refetches to the CURRENT client so previously visited clients'
    // stale caches are not eagerly re-fetched — they refresh on demand when
    // the user switches back. Without scoping, the call count scales with the
    // number of distinct clients visited this session (N × GetTiles +
    // N × GetRolesMapping for N cached clients).
    // Note: ['raw-tiles', clientId] is NOT refetched here explicitly — it is
    // fetched on demand by the ['app-tiles'] and ['header-icon-tiles'] queryFns
    // via ensureRawTiles, which deduplicates concurrent callers through
    // fetchQuery. Refetching it separately would cause a second GetTiles call.
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['clients'] }),
      queryClient.refetchQueries({ queryKey: ['rumRolesForProfile', clientId] }),
      queryClient.refetchQueries({ queryKey: ['app-tiles', clientId] }),
      queryClient.refetchQueries({ queryKey: ['header-icon-tiles', clientId] }),
    ]);

    // Resolve the target client from the RUM list. Angular parity
    // (`getClientsFromRum`, refreshsession=true): if the stored clientId is NOT
    // in the fresh RUM list (e.g. an orchestrator task client the user cannot
    // switch to, left in localStorage after Escalate), fall back to the FIRST
    // available client and warn — Angular sets `currentlySelectedClientId` to
    // `clients[0].guid` in exactly this case. Keeping a client absent from the
    // list would bind the Motif client dropdown to a value with no matching
    // option (console error) and leave the shell on a client the user can't use.
    const freshClients =
      (queryClient.getQueryData(CLIENTS_QUERY_KEY) as ClientInfo[] | undefined) ??
      useClientStore.getState().available;
    let target = freshClients.find((c) => c.id === clientId);
    if (!target) {
      if (freshClients.length === 0) {
        localStorage.removeItem('currentlySelectedClientId');
        hideLoader();
        markInteractionRequired();
        getRouterInstance()?.history.replace('/login');
        return;
      }
      target = freshClients[0];
      clientId = target.id;
      localStorage.setItem('currentlySelectedClientId', clientId);
      resolvedViaFallback = true;
      pushToast({
        variant: 'warning',
        message:
          'You dont have access to the current client. Trying to refresh to the other client',
      });
    }

    // Resolve roles/screens for the validated client.
    const screens = await ensureRoleScreens(clientId);

    // Empty role mapping => user has no screens (Angular's LOGIN_URL path).
    if (!screens || screens.length === 0) {
      pushToast({ variant: 'error', message: 'Client Refreshing failed API response empty' });
      hideLoader();
      markInteractionRequired();
      getRouterInstance()?.history.replace('/login');
      return;
    }

    // Reconcile the store to the validated client (React reads the store, not
    // localStorage, for the dropdown/URL).
    if (useClientStore.getState().current?.id !== clientId) {
      useClientStore.getState().setCurrent(target, { silent: true, source: 'reset' });
    }

    // Seed the SideNavRail's query cache with the freshly-resolved role-screen
    // mapping. The rail renders from `useQuery(['role-screens', clientId])`, whose
    // queryFn (`ensureRoleScreens`) short-circuits on the module-level cache, so a
    // plain `invalidateQueries()` refetch reads the pre-`clearRoleScreens()` value
    // and the rail keeps stale role gating until a hard reload (the header updates
    // because its own queryFn re-fetches the mapping fresh). Writing the validated
    // `screens` to the exact key re-filters the rail in place — the React parity of
    // Angular `SideNavigationComponent.assignMenuItems()` re-running on CLIENT.REFRESH.
    queryClient.setQueryData(['role-screens', clientId], screens);

    eventsModule.emit('shell:refresh-clients', { force: false });
    pushToast({ variant: 'success', message: 'Client refreshed successfully' });
    hideLoader();

    // Keep the current app only if the user still has screen access + a
    // provisioned tile under the new client; otherwise land on client home.
    const router = getRouterInstance();
    if (router) {
      let keepCurrentApp = false;
      if (currentAppId && isScreenAllowed(currentAppId, screens)) {
        await ensureAppRegistry(clientId);
        // Admin apps are gated against a separate tile feed; warm it before the
        // provisioning check so `admin/apps/*` isn't spuriously treated as absent.
        if (currentScreenPath.startsWith('admin/apps')) {
          await ensureAdminAppRegistry(clientId);
        }
        keepCurrentApp = isTileProvisioned(clientId, currentScreenPath);
      }
      if (keepCurrentApp) {
        // Same app under the (possibly new) client: preserve the full path +
        // query, incl. `?popup=`. The popup overlay re-validates its own access
        // on the client change and self-closes if no longer provisioned.
        navigateToClientScope(clientId);
      } else {
        // Bounced off the current app onto client home. Close any open popup MFE
        // too: Angular's `navigateByUrl(redirect)` drops the secondary popup
        // outlet, so a stale overlay must not linger over home (the URL no longer
        // carries `?popup=`, but the store would otherwise re-sync it back).
        closePopupMfe();
        router.history.replace(`/${clientId}/app-container/home`);
      }
    }

    const currentClientId = localStorage.getItem('currentlySelectedClientId');
    if (prevClientId !== currentClientId && currentClientId) {
      void http(Endpoints.updateRecentClient(currentClientId), { method: 'POST' }).catch(() => {});
    }

    // Cross-tab parity for the CLIENT.REFRESH path. Angular's refreshClientList()
    // / CLIENT.REFRESH never broadcast, so a same-client refresh stays silent.
    // Broadcast ONLY when this refresh applied the INTENDED client switch
    // (target resolved, not an access fallback) AND the store's client actually
    // changed — the orchestrator "task under a different client" case that must
    // sync peer tabs. Suppressed on the fallback so a mis-click can't yank every
    // tab onto clients[0]. Loop-safe: the receiver (cross-tab.ts) applies the
    // switch silently and never re-posts or re-enters fullPortalRefresh.
    if (!resolvedViaFallback && currentClientId && currentClientId !== storePrevClientId) {
      postClientRefresh({
        clientId: currentClientId,
        previousClientId: storePrevClientId,
        source: 'reset',
      });
    }

    eventsModule.emit('shell:portal-refresh');
  } catch {
    pushToast({ variant: 'error', message: 'Client refresh failed please try again later' });
    hideLoader();
    markInteractionRequired();
    getRouterInstance()?.history.replace('/login');
  }
}
